// VERA-825: Stripe PaymentElement wrapper for tag shop checkout.
// Card data only flows through Stripe Elements — SAQ-A compliant.
// The `stripe.confirmPayment` call sends card data directly to Stripe;
// our servers never see raw card numbers.

import React, {useState} from 'react';

import {PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {Card, Stack, Typography} from '@mui/material';
import {Button} from '@repo/ui';

interface TagShopPaymentFormProps {
  onSuccess(): void;
  onError(message: string): void;
}

export const TagShopPaymentForm = ({
  onSuccess,
  onError,
}: TagShopPaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setCardError(null);

    const {error} = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // The return_url is required but the payment_intent.succeeded webhook
        // handles fulfillment — the redirect is just a UX landing page.
        return_url: `${window.location.origin}${window.location.pathname}?payment_complete=true`,
      },
      // redirect: 'if_required' prevents a full-page redirect for non-redirect
      // payment methods (cards). When payment succeeds without redirect we call
      // onSuccess directly.
      redirect: 'if_required',
    });

    if (error) {
      const message =
        error.type === 'card_error' || error.type === 'validation_error'
          ? (error.message ?? 'Your card was declined.')
          : 'An unexpected error occurred. Please try again.';
      setCardError(message);
      onError(message);
    } else {
      // Payment succeeded (no redirect required)
      onSuccess();
    }

    setIsProcessing(false);
  };

  return (
    <Card>
      <Stack
        spacing={4}
        p={6}
        component="form"
        id="tag-shop-payment-form"
        onSubmit={handleSubmit}
      >
        <Typography variant="body1">Payment</Typography>

        <PaymentElement
          id="tag-shop-payment-element"
          options={{layout: 'tabs'}}
        />

        {cardError && (
          <Typography variant="body3" color="error.main">
            {cardError}
          </Typography>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={isProcessing || !stripe || !elements}
        >
          {isProcessing ? 'Processing…' : 'Pay now'}
        </Button>

        <Typography variant="body3" color="text.secondary" textAlign="center">
          Payments are processed securely by Stripe. Your card details are never
          stored on our servers.
        </Typography>
      </Stack>
    </Card>
  );
};
